import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ChartProvider from '../../../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../../../charts/echarts/ColorProvider'
import Tooltip from '../../../../../charts/echarts/Tooltip'
import PieChart from '../../../../../charts/echarts/PieChart'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { buildSliceTooltipFormatter } from './allocationTooltip'

// Donut wrapper. Reads format from props (the tool computes
// `effectiveFormat(...)` for the selected measure once and passes it
// down) so tooltips render the same way the grid cells would.
//
// Slice angles use `abs` (chart can't render negatives); the tooltip
// shows the actual signed value via the `raw` field on each slice.
// Percent in the tooltip is computed off `totalAbs` so it always sums
// to 100% across visible slices.
export default function AllocationDonut ({ slices, valueFormat, height = 340 }) {
  const { formatter: fmt } = useFormattingContext()
  const tooltipFormatter = useMemo(
    () => buildSliceTooltipFormatter(fmt, valueFormat),
    [fmt, valueFormat]
  )

  // ECharts pie expects { name, value }. We pass through `raw` and
  // `percent` as extra fields so the tooltip can read them. `id` on
  // each data item carries the row's stable `groupId` when present
  // so ECharts keeps animation continuity across re-renders even
  // when two rows share a display `name`.
  const chartData = slices.map(s => ({
    id: s.groupId != null ? String(s.groupId) : s.name,
    name: s.name,
    value: s.abs,
    raw: s.raw,
    percent: s.percent
  }))

  return (
    <ChartColorProvider theme='default'>
      <ChartProvider height={height} loadingTemplate='pie'>
        <Tooltip trigger='item' formatter={tooltipFormatter} />
        <PieChart
          seriesId='allocation'
          data={chartData}
          radius={['55%', '80%']}
          padAngle={1}
          minShowLabelAngle={6}
          label={{ show: true, position: 'outside', formatter: '{b}' }}
          labelLine={{ show: true, smooth: true }}
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

AllocationDonut.propTypes = {
  slices: PropTypes.arrayOf(PropTypes.shape({
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    raw: PropTypes.number.isRequired,
    abs: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  })).isRequired,
  valueFormat: PropTypes.string,
  height: PropTypes.number
}
